import { useCallback, useEffect, useState } from 'react';
import { Badge, Eyebrow, MetaRow, token } from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { PASSKEYS_COPY } from '@/components/Passkeys/passkeys.copy';
import { deletePasskey, getPasskeys, renamePasskey } from '@/lib/api';
import type { PasskeyView } from '@bp-files/client';

/**
 * The passkey account screen (SPEC §9.3, USR-11).
 *
 * **Not a sixth surface.** `surfaces.ts` holds the five things the product is
 * *about*; this is a screen about the person looking at it, reached from the
 * account menu rather than the nav rail. Adding it to `SURFACES` would put it
 * in the rail beside Files and Users, where it would read as another place to
 * do work.
 *
 * **The device question is a step, not a checkbox**, and that is the one piece
 * of layout worth arguing about. It decides what kind of credential can exist
 * and it cannot be revised afterwards — so it gets its own screen with its own
 * explanation, rather than a toggle somebody clicks past on the way to the
 * button they wanted. `passkeys.copy.ts` carries why it offers no "not sure".
 *
 * **Enrolment goes through `authDriver`, never through `lib/api.ts`.** It is a
 * ceremony rather than a request: the browser and the operating system do the
 * middle of it, and routing it through the driver is what keeps the WebAuthn
 * library out of a mocked build (ADR-0027). The three ordinary reads and writes
 * — list, rename, remove — are plain endpoints and live in `api.ts` with
 * everything else.
 *
 * **Management only, and nothing navigates here** (ADR-0051). This screen used
 * to carry two things that were about *signing in* rather than about credentials:
 * a restricted session (SYS-10) was redirected here on arrival, and an "Use the
 * passkey you already have" control was supposed to lift the restriction. That
 * control had never worked in a deployed build — `App.tsx` handed it the
 * assertion with none of the `canHoldSession` guard `Bootstrap.tsx` applies to
 * the same function, so cross-origin the ceremony verified, the browser
 * discarded the cookie, and the reload landed back on this screen. Both acts
 * belong to the sign-in landing page now, which is where the session is decided
 * and where the guard already lives.
 */

export interface PasskeysProps {
  /** From `authDriver`. Injected so this component never touches `navigator`. */
  readonly enroll: (input: { label: string; sharedDevice: boolean }) => Promise<{
    passkeyCount: number;
  }>;
  /**
   * Whether this device has a built-in authenticator.
   *
   * Phrases the question — it never answers it. Somebody at a shared squad-room
   * workstation is sitting at a machine that reports `true`, which is exactly
   * the case USR-11 exists for.
   */
  readonly platformAvailable: () => Promise<boolean>;
}

type Stage =
  | { kind: 'list' }
  | { kind: 'question' }
  | { kind: 'naming'; sharedDevice: boolean }
  | { kind: 'removing'; passkey: PasskeyView };

export function Passkeys({ enroll, platformAvailable }: PasskeysProps) {
  const [passkeys, setPasskeys] = useState<PasskeyView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<Stage>({ kind: 'list' });
  const [busy, setBusy] = useState(false);
  const [hasPlatform, setHasPlatform] = useState(false);
  const [promptSecond, setPromptSecond] = useState(false);
  const [renaming, setRenaming] = useState<string | undefined>(undefined);

  /**
   * Reload the list.
   *
   * **A token rather than a call**, following `useRecycleBin` and every other
   * async read in this app: the effect owns the request and its `AbortController`,
   * and a caller asks for another one by moving the token. Calling an async
   * loader straight out of an effect is what React's `set-state-in-effect` rule
   * refuses, and it is right to — the setters would run against a component that
   * may have unmounted.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    getPasskeys(controller.signal)
      .then((body) => {
        setPasskeys(body.passkeys);
        setError(undefined);
      })
      .catch((cause: unknown) => {
        // An abort is our own doing, not a failure worth reporting.
        if (cause instanceof Error && cause.name === 'AbortError') return;
        setError(messageFrom(cause, PASSKEYS_COPY.errors.load));
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  useEffect(() => {
    let live = true;

    platformAvailable()
      .then((available) => {
        if (live) setHasPlatform(available);
      })
      .catch(() => {
        // A browser with no WebAuthn at all. The question still gets asked; it
        // simply loses the sentence about this device's own fingerprint.
      });

    return () => {
      live = false;
    };
  }, [platformAvailable]);

  const create = useCallback(
    async (label: string, sharedDevice: boolean) => {
      setBusy(true);
      setError(undefined);
      try {
        const result = await enroll({ label, sharedDevice });
        setStage({ kind: 'list' });
        // USR-11a rung 0. Asked once, immediately, and only when it is the
        // answer — a person with two already does not need telling.
        setPromptSecond(result.passkeyCount === 1);
        reload();
      } catch (cause) {
        // A cancelled browser dialog is `NotAllowedError`, and it is not a
        // failure — the person changed their mind. Reporting it as one would
        // teach people that this screen is broken.
        const cancelled = cause instanceof Error && cause.name === 'NotAllowedError';
        setError(
          cancelled
            ? PASSKEYS_COPY.errors.cancelled
            : messageFrom(cause, PASSKEYS_COPY.errors.create),
        );
        setStage({ kind: 'list' });
      } finally {
        setBusy(false);
      }
    },
    [enroll, reload],
  );

  const remove = useCallback(
    async (credentialId: string) => {
      setBusy(true);
      try {
        await deletePasskey(credentialId);
        setStage({ kind: 'list' });
        reload();
      } catch (cause) {
        setError(messageFrom(cause, PASSKEYS_COPY.errors.remove));
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const rename = useCallback(
    async (credentialId: string, label: string) => {
      try {
        await renamePasskey(credentialId, label);
        setRenaming(undefined);
        reload();
      } catch (cause) {
        setError(messageFrom(cause, PASSKEYS_COPY.errors.rename));
      }
    },
    [reload],
  );

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Eyebrow>{PASSKEYS_COPY.title}</Eyebrow>
        <h1 className="text-2xl font-semibold">{PASSKEYS_COPY.list.heading}</h1>
        <p className="max-w-prose text-sm" style={{ color: token('bp-text-secondary') }}>
          {PASSKEYS_COPY.intro}
        </p>
      </header>

      {error === undefined ? null : (
        <p role="alert" className="text-sm" style={{ color: token('bp-danger') }}>
          {error}
        </p>
      )}

      {stage.kind === 'question' ? (
        <DeviceQuestion
          hasPlatform={hasPlatform}
          onAnswer={(sharedDevice) => {
            setStage({ kind: 'naming', sharedDevice });
          }}
          onCancel={() => {
            setStage({ kind: 'list' });
          }}
        />
      ) : null}

      {stage.kind === 'naming' ? (
        <NameForm
          busy={busy}
          onCreate={(label) => {
            void create(label, stage.sharedDevice);
          }}
          onCancel={() => {
            setStage({ kind: 'list' });
          }}
        />
      ) : null}

      {stage.kind === 'removing' ? (
        <RemoveConfirm
          passkey={stage.passkey}
          lastOne={(passkeys ?? []).length === 1}
          busy={busy}
          onConfirm={() => {
            void remove(stage.passkey.credentialId);
          }}
          onCancel={() => {
            setStage({ kind: 'list' });
          }}
        />
      ) : null}

      {stage.kind === 'list' ? (
        <>
          {promptSecond ? (
            <div
              className="flex flex-col gap-2 border p-3"
              style={{
                borderColor: token('bp-border-strong'),
                borderRadius: token('radius-md'),
              }}
            >
              <p className="max-w-prose text-sm">{PASSKEYS_COPY.add.second}</p>
              <div className="flex gap-2">
                <PrimaryButton
                  onClick={() => {
                    setStage({ kind: 'question' });
                  }}
                >
                  {PASSKEYS_COPY.add.action}
                </PrimaryButton>
                <QuietButton
                  onClick={() => {
                    setPromptSecond(false);
                  }}
                >
                  {PASSKEYS_COPY.add.dismissSecond}
                </QuietButton>
              </div>
            </div>
          ) : null}

          <PasskeyList
            passkeys={passkeys}
            renaming={renaming}
            onRename={(credentialId) => {
              setRenaming(credentialId);
            }}
            onRenameCancel={() => {
              setRenaming(undefined);
            }}
            onRenameSave={(credentialId, label) => {
              void rename(credentialId, label);
            }}
            onRemove={(passkey) => {
              setStage({ kind: 'removing', passkey });
            }}
          />

          {promptSecond ? null : (
            <div>
              <PrimaryButton
                onClick={() => {
                  setStage({ kind: 'question' });
                }}
              >
                {PASSKEYS_COPY.add.action}
              </PrimaryButton>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function PasskeyList({
  passkeys,
  renaming,
  onRename,
  onRenameCancel,
  onRenameSave,
  onRemove,
}: {
  readonly passkeys: PasskeyView[] | undefined;
  readonly renaming: string | undefined;
  readonly onRename: (credentialId: string) => void;
  readonly onRenameCancel: () => void;
  readonly onRenameSave: (credentialId: string, label: string) => void;
  readonly onRemove: (passkey: PasskeyView) => void;
}) {
  if (passkeys === undefined) return null;

  if (passkeys.length === 0) {
    return (
      <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {PASSKEYS_COPY.list.empty}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {passkeys.map((passkey) => (
        <li
          key={passkey.credentialId}
          className="flex flex-col gap-2 border p-3"
          style={{ borderColor: token('bp-border'), borderRadius: token('radius-md') }}
        >
          {renaming === passkey.credentialId ? (
            <RenameForm
              current={passkey.label}
              onSave={(label) => {
                onRenameSave(passkey.credentialId, label);
              }}
              onCancel={onRenameCancel}
            />
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium">{passkey.label}</span>
              <div className="flex gap-2">
                <QuietButton
                  onClick={() => {
                    onRename(passkey.credentialId);
                  }}
                >
                  {PASSKEYS_COPY.list.rename}
                </QuietButton>
                <QuietButton
                  onClick={() => {
                    onRemove(passkey);
                  }}
                >
                  {PASSKEYS_COPY.list.remove}
                </QuietButton>
              </div>
            </div>
          )}

          {/*
            Amber is the design system's colour for time and nothing else, which
            is why `MetaRow` takes the timestamp as its own prop rather than as
            one item in a list.
          */}
          <MetaRow
            time={passkey.createdAt.slice(0, 10)}
            dateTime={passkey.createdAt}
            label={PASSKEYS_COPY.list.added}
            trailing={
              <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
                {passkey.lastUsedAt === undefined
                  ? PASSKEYS_COPY.list.neverUsed
                  : `${PASSKEYS_COPY.list.lastUsed} ${passkey.lastUsedAt.slice(0, 10)}`}
              </span>
            }
          />

          <div className="flex flex-wrap gap-2">
            {/*
              Surfaced, never governed (§14). A credential synced to a consumer
              cloud has its private key in an account Blue Partner does not
              control, which an agency may object to — so the person is told,
              and no policy knob exists until somebody asks for one.
            */}
            {passkey.backedUp ? <Badge>{PASSKEYS_COPY.list.synced}</Badge> : null}
            {passkey.sharedDevice ? <Badge>{PASSKEYS_COPY.list.sharedDevice}</Badge> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function DeviceQuestion({
  hasPlatform,
  onAnswer,
  onCancel,
}: {
  readonly hasPlatform: boolean;
  readonly onAnswer: (sharedDevice: boolean) => void;
  readonly onCancel: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 border p-4"
      style={{ borderColor: token('bp-border-strong'), borderRadius: token('radius-md') }}
    >
      <h2 className="text-lg font-medium">{PASSKEYS_COPY.question.heading}</h2>
      <p className="max-w-prose text-sm" style={{ color: token('bp-text-secondary') }}>
        {PASSKEYS_COPY.question.help}
      </p>

      <div className="flex flex-col gap-2">
        {/*
          The "own device" answer is offered whether or not this machine has a
          built-in authenticator: a security key on your own laptop is a
          perfectly ordinary arrangement, and hiding the option would turn a
          capability check into an answer to a question only the person can
          answer.
        */}
        <AnswerButton
          title={PASSKEYS_COPY.question.own}
          help={hasPlatform ? PASSKEYS_COPY.question.ownHelp : undefined}
          onClick={() => {
            onAnswer(false);
          }}
        />
        <AnswerButton
          title={PASSKEYS_COPY.question.shared}
          help={PASSKEYS_COPY.question.sharedHelp}
          onClick={() => {
            onAnswer(true);
          }}
        />
      </div>

      <div>
        <QuietButton onClick={onCancel}>{PASSKEYS_COPY.question.cancel}</QuietButton>
      </div>
    </div>
  );
}

function AnswerButton({
  title,
  help,
  onClick,
}: {
  readonly title: string;
  readonly help: string | undefined;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-1 border p-3 text-left transition-colors"
      style={{ borderColor: token('bp-border'), borderRadius: token('radius-md') }}
    >
      <span className="text-sm font-medium">{title}</span>
      {help === undefined ? null : (
        <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {help}
        </span>
      )}
    </button>
  );
}

function NameForm({
  busy,
  onCreate,
  onCancel,
}: {
  readonly busy: boolean;
  readonly onCreate: (label: string) => void;
  readonly onCancel: () => void;
}) {
  const [label, setLabel] = useState('');

  return (
    <form
      className="flex flex-col gap-3 border p-4"
      style={{ borderColor: token('bp-border-strong'), borderRadius: token('radius-md') }}
      onSubmit={(event) => {
        event.preventDefault();
        onCreate(label.trim());
      }}
    >
      <h2 className="text-lg font-medium">{PASSKEYS_COPY.naming.heading}</h2>
      <label className="flex flex-col gap-1 text-sm">
        <span style={{ color: token('bp-text-secondary') }}>{PASSKEYS_COPY.naming.help}</span>
        <input
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
          }}
          placeholder={PASSKEYS_COPY.naming.placeholder}
          className="border px-2 py-1.5 text-sm"
          style={{ borderColor: token('bp-border'), borderRadius: token('radius-md') }}
        />
      </label>
      <div className="flex gap-2">
        <PrimaryButton type="submit" disabled={busy || label.trim() === ''}>
          {PASSKEYS_COPY.naming.create}
        </PrimaryButton>
        <QuietButton onClick={onCancel}>{PASSKEYS_COPY.naming.cancel}</QuietButton>
      </div>
    </form>
  );
}

function RenameForm({
  current,
  onSave,
  onCancel,
}: {
  readonly current: string;
  readonly onSave: (label: string) => void;
  readonly onCancel: () => void;
}) {
  const [label, setLabel] = useState(current);

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(label.trim());
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        <span style={{ color: token('bp-text-secondary') }}>{PASSKEYS_COPY.list.nameLabel}</span>
        <input
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
          }}
          className="border px-2 py-1 text-sm"
          style={{ borderColor: token('bp-border'), borderRadius: token('radius-md') }}
        />
      </label>
      <PrimaryButton type="submit" disabled={label.trim() === ''}>
        {PASSKEYS_COPY.list.save}
      </PrimaryButton>
      <QuietButton onClick={onCancel}>{PASSKEYS_COPY.list.cancel}</QuietButton>
    </form>
  );
}

function RemoveConfirm({
  passkey,
  lastOne,
  busy,
  onConfirm,
  onCancel,
}: {
  readonly passkey: PasskeyView;
  readonly lastOne: boolean;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 border p-4"
      style={{ borderColor: token('bp-border-strong'), borderRadius: token('radius-md') }}
    >
      <h2 className="text-lg font-medium">{PASSKEYS_COPY.removing.heading}</h2>
      <p className="text-sm font-medium">{passkey.label}</p>
      {/*
        Stated because people do not expect it, and it is deliberate: removing a
        credential ends every session that person holds. The act somebody
        performs when a laptop is stolen is *remove the passkey*, and a thief
        left signed in for twelve hours would make that act useless.
      */}
      <p className="max-w-prose text-sm" style={{ color: token('bp-text-secondary') }}>
        {PASSKEYS_COPY.removing.warning}
      </p>
      {lastOne ? (
        <p className="max-w-prose text-sm" style={{ color: token('bp-text-secondary') }}>
          {PASSKEYS_COPY.removing.lastOne}
        </p>
      ) : null}
      <div className="flex gap-2">
        <PrimaryButton onClick={onConfirm} disabled={busy}>
          {PASSKEYS_COPY.removing.confirm}
        </PrimaryButton>
        <QuietButton onClick={onCancel}>{PASSKEYS_COPY.removing.cancel}</QuietButton>
      </div>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
}: {
  readonly children: string;
  readonly onClick?: () => void;
  readonly type?: 'button' | 'submit';
  readonly disabled?: boolean;
}) {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-1.5 text-sm font-medium transition-opacity disabled:opacity-50"
      style={{
        background: token('bp-blue'),
        // `bp-on-accent`, not `bp-text` — the readme's line for solid blue
        // buttons, and the pairing every `primaryButtonStyle()` in this repo
        // already uses. Near-black ink on daylight's blue is 3.2:1 (ADR-0064).
        color: token('bp-on-accent'),
        borderRadius: token('radius-md'),
      }}
    >
      {children}
    </button>
  );
}

function QuietButton({
  children,
  onClick,
}: {
  readonly children: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border px-3 py-1.5 text-sm font-medium transition-colors"
      style={{
        borderColor: token('bp-border-strong'),
        color: token('bp-text'),
        borderRadius: token('radius-md'),
      }}
    >
      {children}
    </button>
  );
}
